/**
 * PowerSync Client-Side Schema
 * 
 * Определяет структуру локальной SQLite базы данных PowerSync.
 * Эта схема ДОЛЖНА соответствовать Sync Rules, задеплоенным в PowerSync Dashboard.
 * 
 * ВАЖНО: Колонка `id` создаётся автоматически как TEXT PRIMARY KEY.
 * Не нужно её объявлять в схеме.
 * 
 * Типы: column.text, column.integer, column.real
 */

import { column, Schema, Table } from '@powersync/common';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 📊 Expenses (Расходы)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const expenses = new Table(
  {
    user_id: column.integer,
    title: column.text,
    amount: column.real,
    category: column.text,
    category_id: column.integer,
    payment_date: column.text,
    description: column.text,
    payment_method_id: column.integer,
    created_at: column.text,
    goal_id: column.integer,
    is_monthly: column.integer, // 0=one-time, 1=monthly, 2=goal deposit, 3=weekly
    is_internal_transfer: column.integer, // boolean → 0/1
    receipt_image: column.text,
    receipt_items: column.text, // JSON string
    receipt_tax: column.text, // numeric → text in PowerSync
    receipt_tip: column.text,
    receipt_service_fee: column.text,
    receipt_discount: column.text,
    metadata: column.text, // JSON string
    recurring_expense_id: column.integer,
    input_method: column.text,
    // ── Recurring-specific fields (зеркалят PostgreSQL expenses) ──
    day: column.integer, // день недели (weekly) или дата месяца (monthly/yearly)
    month: column.integer, // месяц (yearly)
    start_date_recurring: column.text, // дата начала повторений
    notification_time: column.text, // время уведомления (HH:MM)
    disable_payment_notifications: column.integer, // boolean → 0/1
    reminder_times: column.text, // JSON array
    reminder_offsets: column.text, // JSON array
    reminders_enabled: column.integer, // boolean → 0/1
    is_active: column.integer, // boolean → 0/1 (для recurring)
    next_notification_date: column.text, // ISO UTC timestamp — когда следующий раз сработает recurring
  },
  {
    indexes: {
      by_user: ['user_id'],
      by_date: ['payment_date'],
      by_category: ['category_id'],
      by_user_date: ['user_id', 'payment_date'],
      by_monthly: ['is_monthly'],
    },
  }
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 💰 Income Transactions (Доходы)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const income_transactions = new Table(
  {
    user_id: column.integer,
    title: column.text,
    amount: column.real,
    category: column.text,
    category_id: column.integer,
    transaction_date: column.text,
    description: column.text,
    payment_method_id: column.integer,
    is_internal_transfer: column.integer, // boolean → 0/1
    receipt_image: column.text,
    receipt_items: column.text, // JSON string
    receipt_tax: column.text, // numeric → text in PowerSync
    receipt_tip: column.text,
    receipt_service_fee: column.text,
    receipt_discount: column.text,
    metadata: column.text, // JSON string
    recurring_income_id: column.integer,
    input_method: column.text,
  },
  {
    indexes: {
      by_user: ['user_id'],
      by_date: ['transaction_date'],
      by_user_date: ['user_id', 'transaction_date'],
    },
  }
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 📁 Categories (Категории)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const categories = new Table(
  {
    user_id: column.integer,
    name: column.text,
    loc_key: column.text,
    emoji: column.text,
    description_for_llm: column.text,
    is_system: column.integer, // boolean → 0/1
    sort_order: column.integer,
    created_at: column.text,
    parent_id: column.integer,
    category_level: column.text, // 'primary' | 'subcategory'
    plaid_key: column.text,
    is_premium_only: column.integer, // 0/1 — boolean
    _pending_delete: column.integer, // 0/1 — soft-delete флаг (client-only)
    _delete_target_id: column.text,  // ID целевой категории для переноса транзакций (client-only)
    user_color: column.text, // HEX цвет пользователя (e.g. '#FF9F66'), null = наследовать от родителя или дефолт
  },
  {
    indexes: {
      by_user: ['user_id'],
      by_system: ['is_system'],
      by_parent: ['parent_id'],
    },
  }
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🎯 Goals (Цели)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const goals = new Table(
  {
    user_id: column.integer,
    name: column.text,
    target_amount: column.real,
    current_amount: column.real,
    icon: column.text,
    status: column.text,
    created_at: column.text,
    deadline: column.text,
    achieved_at: column.text,
    closed_at: column.text,
    deposit_limit: column.real,
    close_payment_method_id: column.text, // client-only: ID счёта для возврата средств при закрытии
  },
  {
    indexes: {
      by_user: ['user_id'],
      by_status: ['status'],
    },
  }
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 📏 Category Limits (Лимиты по категориям)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const category_limits = new Table(
  {
    user_id: column.integer,
    category_id: column.integer,
    category_name: column.text,
    limit_amount: column.real,
    period: column.text,
    start_month: column.text, // "YYYY-MM" — пользовательский начальный месяц для многомесячных периодов (null = скользящий от текущего)
    created_at: column.text,
    updated_at: column.text,
    _pending_delete: column.integer, // soft-delete flag (0 или 1) — для корректной синхронизации с сервером
  },
  {
    indexes: {
      by_user: ['user_id'],
      by_category: ['category_id'],
    },
  }
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 💳 Payment Methods (Платёжные методы / Счета)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const payment_methods = new Table(
  {
    user_id: column.integer,
    name: column.text,
    type: column.text,
    subtype: column.text,
    balance: column.real,
    is_default: column.integer, // boolean → 0/1
    is_active: column.integer, // boolean → 0/1
    created_at: column.text,
    institution_name: column.text,
    source: column.text,
    _transfer_target_id: column.text, // ID счёта для переноса при удалении (client-only)
  },
  {
    indexes: {
      by_user: ['user_id'],
      by_active: ['is_active'],
    },
  }
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🏦 Plaid Transactions (Банковские транзакции)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const plaid_transactions = new Table(
  {
    user_id: column.integer,
    plaid_transaction_id: column.text,
    item_id: column.text,
    account_id: column.text,
    amount: column.text, // numeric → text in PowerSync
    date: column.text,
    datetime: column.text, // ISO 8601 с временем (YYYY-MM-DDTHH:mm:ssZ), если банк предоставляет
    authorized_date: column.text,
    name: column.text,
    merchant_name: column.text,
    // category убран — это ARRAY тип, не поддерживается PowerSync
    primary_category: column.text,
    subcategory: column.text,
    iso_currency_code: column.text,
    pending: column.integer, // boolean → 0/1
    payment_channel: column.text,
    is_internal_transfer: column.integer, // boolean → 0/1
    is_recurring_manual: column.integer, // boolean → 0/1
    user_corrected_category: column.text,
    category_id: column.integer,
    logo_url: column.text,
    enriched_merchant_name: column.text,
    created_at: column.text,
  },
  {
    indexes: {
      by_user: ['user_id'],
      by_date: ['date'],
      by_account: ['account_id'],
      by_plaid_id: ['plaid_transaction_id'],
    },
  }
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🏦 Plaid Accounts (Банковские счета)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const plaid_accounts = new Table(
  {
    user_id: column.integer,
    item_id: column.text,
    account_id: column.text,
    name: column.text,
    official_name: column.text,
    custom_name: column.text,           // user-defined account name (null = use original)
    type: column.text,
    subtype: column.text,
    mask: column.text,
    balance_current: column.text, // numeric → text in PowerSync
    balance_available: column.text,
    currency_code: column.text,
    is_active: column.integer, // boolean → 0/1
    // 💳 Liability fields (credit card payment info from Plaid Liabilities API)
    minimum_payment_amount: column.text,    // numeric → text in PowerSync
    next_payment_due_date: column.text,
    last_statement_balance: column.text,    // numeric → text in PowerSync
    last_statement_issue_date: column.text,
    is_overdue: column.integer,            // boolean → 0/1
    created_at: column.text,
    updated_at: column.text,
  },
  {
    indexes: {
      by_user: ['user_id'],
      by_item: ['item_id'],
    },
  }
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🔄 Recurring Income Definitions (Регулярные доходы)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const recurring_income_definitions = new Table(
  {
    user_id: column.integer,
    title: column.text,
    default_amount: column.real,
    frequency: column.text, // 'daily' | 'weekly' | 'monthly' | 'yearly'
    day_of_week: column.integer,
    day_of_month: column.integer,
    start_date: column.text,
    notification_time: column.text,
    // timezone убран — нет в PostgreSQL таблице
    reminder_times: column.text, // JSON array
    reminder_offsets: column.text, // JSON array
    reminders_enabled: column.integer, // boolean → 0/1
    description: column.text,
    payment_method_id: column.integer,
    is_active: column.integer, // boolean → 0/1
    next_notification_date: column.text,
    created_at: column.text,
  },
  {
    indexes: {
      by_user: ['user_id'],
      by_active: ['is_active'],
    },
  }
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 💰 Goal Deposits (Шаблоны автопополнений целей)
// Зеркалит PostgreSQL таблицу goal_deposits
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const goal_deposits = new Table(
  {
    user_id: column.integer,
    goal_id: column.integer,
    title: column.text,
    amount: column.real,
    frequency: column.text, // 'daily' | 'weekly' | 'monthly'
    day_of_week: column.integer,
    day_of_month: column.integer,
    start_date: column.text,
    next_deposit_date: column.text,
    notification_time: column.text, // HH:MM
    is_active: column.integer, // boolean → 0/1
    created_at: column.text,
    payment_method_id: column.integer, // Добавлено — есть в Sync Rules
  },
  {
    indexes: {
      by_goal: ['goal_id'],
      by_user: ['user_id'],
      by_active: ['is_active'],
    },
  }
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🎨 User Category Colors (Пользовательские цвета категорий)
// Хранит индивидуальные настройки цветов для категорий
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const user_category_colors = new Table(
  {
    user_id: column.text,
    category_id: column.text, // ID категории (integer string для системных, UUID для пользовательских)
    color: column.text, // HEX цвет e.g. '#FF9F66'
    created_at: column.text,
    updated_at: column.text,
  },
  {
    indexes: {
      by_user: ['user_id'],
      by_category: ['category_id'],
      by_user_category: ['user_id', 'category_id'],
    },
  }
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ⚙️ User Settings (Настройки пользователя)
// Зеркалит колонки из PostgreSQL таблицы users
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const user_settings = new Table(
  {
    user_id: column.integer,
    language_code: column.text,
    currency_code: column.text,
    timezone: column.text,
    timezone_city: column.text,
    first_name: column.text,
    last_name: column.text,
    balance_hidden: column.integer, // boolean → 0/1
    biometric_enabled: column.integer, // boolean → 0/1
    onboarding_completed: column.integer, // boolean → 0/1
    avatar_url: column.text,
    manual_accounts_order: column.text, // JSON array
    plaid_institutions_order: column.text, // JSON array
    plaid_accounts_order: column.text, // JSON object
    theme: column.text,
    offline_notice_dismissed: column.integer, // boolean → 0/1
    widget_accounts_order: column.text, // JSON array
    pending_warning_dismissed: column.integer, // boolean → 0/1
  },
  {
    indexes: {
      by_user: ['user_id'],
    },
  }
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 📦 Export Schema
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export const AppSchema = new Schema({
  expenses,
  income_transactions,
  categories,
  goals,
  category_limits,
  payment_methods,
  plaid_transactions,
  plaid_accounts,
  recurring_income_definitions,
  goal_deposits,
  user_settings,
  user_category_colors,
});

// Типы для TypeScript
export type Database = (typeof AppSchema)['types'];
export type ExpenseRecord = Database['expenses'];
export type IncomeRecord = Database['income_transactions'];
export type CategoryRecord = Database['categories'];
export type GoalRecord = Database['goals'];
export type CategoryLimitRecord = Database['category_limits'];
export type PaymentMethodRecord = Database['payment_methods'];
export type PlaidTransactionRecord = Database['plaid_transactions'];
export type PlaidAccountRecord = Database['plaid_accounts'];
export type RecurringIncomeDefinitionRecord = Database['recurring_income_definitions'];
export type GoalDepositRecord = Database['goal_deposits'];
export type UserSettingsRecord = Database['user_settings'];
export type UserCategoryColorRecord = Database['user_category_colors'];
